import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@config/env";

const SEGMENTS = [
  { key:"available", label:"Available", color:"#16A34A", min:20 },
  { key:"partial",   label:"Partial",   color:"#D97706", min:10 },
  { key:"busy",      label:"Busy",      color:"#EA580C", min:0  },
];

function DonutChart({ segments, size=120 }) {
  const cx = size / 2, cy = size / 2;
  const R = size * 0.38, r = size * 0.24;
  const total = segments.reduce((s, x) => s + x.value, 0);
  if (!total) return (
    <svg width={size} height={size}>
      <circle cx={cx} cy={cy} r={R} fill="none" style={{ stroke:"var(--fc-border-light)" }} strokeWidth={R - r} />
    </svg>
  );

  let angle = -Math.PI / 2;
  const arcs = segments.map(seg => {
    const frac = seg.value / total;
    const start = angle;
    angle += frac * Math.PI * 2;
    return { ...seg, start, end: angle, frac };
  });

  const arc = (s, e, rr) => {
    const x1 = cx + rr * Math.cos(s), y1 = cy + rr * Math.sin(s);
    const x2 = cx + rr * Math.cos(e), y2 = cy + rr * Math.sin(e);
    const large = e - s > Math.PI ? 1 : 0;
    return `M${x1},${y1} A${rr},${rr},0,${large},1,${x2},${y2}`;
  };

  return (
    <svg width={size} height={size}>
      {arcs.map((a, i) => {
        if (a.frac === 0) return null;
        const gap = 0.04;
        const start = a.start + gap, end = a.end - gap;
        return (
          <path key={i} fill="none" stroke={a.color} strokeWidth={R - r} strokeLinecap="round"
            d={arc(start, end, (R + r) / 2)}
            onClick={a.onClick} style={{ cursor: a.onClick ? "pointer" : "default" }}>
            {a.onClick && <title>{`View ${a.label.toLowerCase()} resources in Resources`}</title>}
          </path>
        );
      })}
      {/* Center label */}
      <text x={cx} y={cy + size * 0.07} textAnchor="middle" fontSize={size * 0.22} fontWeight="800" style={{ fill:"var(--fc-text-primary)" }}>{total}</text>
    </svg>
  );
}

export default function AvailabilityDonut({ userSlug, loading: parentLoading }) {
  const navigate = useNavigate();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userSlug) return;
    const fd = new FormData();
    fd.append("user_slug", userSlug);
    fd.append("filter_text", "");
    fd.append("limit", "200");
    fd.append("offset", "0");
    axios.post(`${API_BASE}get-my-resource-listing`, fd)
      .then(res => {
        const list = res.data?.resourceList || [];
        const counts = { available:0, partial:0, busy:0 };
        list.forEach(r => {
          const v = Number(r.availability || 0);
          if (v >= 20)      counts.available++;
          else if (v >= 10) counts.partial++;
          else              counts.busy++;
        });
        setData(counts);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [userSlug]);

  if (loading || parentLoading) return (
    <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-card-border)", borderRadius:16, padding:"20px", boxShadow:"var(--fc-card-shadow)", display:"flex", gap:20, alignItems:"center" }}>
      <div style={{ width:120, height:120, borderRadius:"50%", background:"var(--fc-border-light)" }} />
      <div style={{ flex:1 }}>
        {[55,70,45].map((w,i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
            <div style={{ width:10, height:10, borderRadius:"50%", background:"var(--fc-border-light)", flexShrink:0 }} />
            <div style={{ height:10, background:"var(--fc-border-light)", borderRadius:4, width:`${w}%` }} />
          </div>
        ))}
      </div>
    </div>
  );

  const segments = SEGMENTS.map(s => ({ ...s, value: data?.[s.key] || 0, onClick: () => navigate(`/resources?availability=${s.key}`) }));
  const total    = segments.reduce((a, b) => a + b.value, 0);

  return (
    <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-card-border)", borderRadius:16, padding:"20px", boxShadow:"var(--fc-card-shadow)" }}>
      <div style={{ fontSize:13, fontWeight:700, color:"var(--fc-text-primary)", marginBottom:14, display:"flex", alignItems:"center", gap:7 }}>
        <i className="fa-solid fa-circle-half-stroke" style={{ color:"var(--fc-primary)", fontSize:13 }} />
        Availability Breakdown
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:20 }}>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
          <DonutChart segments={segments} size={120} />
          <span style={{ fontSize:11, color:"var(--fc-text-subtle)" }}>resources</span>
        </div>
        <div style={{ flex:1, display:"flex", flexDirection:"column", gap:10 }}>
          {segments.map(seg => {
            const pct = total > 0 ? Math.round((seg.value / total) * 100) : 0;
            return (
              <div key={seg.key}
                onClick={seg.onClick}
                style={{ cursor:"pointer" }}
                title={`View ${seg.label.toLowerCase()} resources in Resources`}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:seg.color, flexShrink:0 }} />
                    <span style={{ fontSize:12, color:"var(--fc-text-secondary)", fontWeight:500 }}>{seg.label}</span>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ fontSize:12, fontWeight:700, color:"var(--fc-text-primary)" }}>{seg.value}</span>
                    <span style={{ fontSize:10, color:"var(--fc-text-subtle)" }}>{pct}%</span>
                  </div>
                </div>
                <div style={{ height:4, background:"var(--fc-border-light)", borderRadius:99, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${pct}%`, background:seg.color, borderRadius:99, transition:"width 0.6s ease" }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
